import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Button,
  CardHeader,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import StandardSkeletoon from "../../../components/reusable/loader/StandardSkeletoon";
import { TYPE_NOTIFICATION } from "../../../components/reusable/NotificationBull";
import useConfirm from "../../../hooks/useConfirm";
import useToast from "../../../hooks/useToast";
import { HistoriqueevaluationService } from "../../../services/HistoriqueevaluationService";
import {
  evaluation_refonte_fiche_evaluation_service,
  evaluation_refonte_jalon_service,
  evaluation_refonte_reponse_service,
  evaluation_refonte_saison_service,
} from "../../../services/PersonalEvaluationRefonteService";
import UserService from "../../../services/UserService";
import SliderNotation from "./comps/SliderNotation";
import { findOutEvaluationGenerale } from "./EvaluationExaminationFormResume";
import RecapEvaluationForm from "./RecapEvaluationForm";

const TYPE_REPONSE = {
  comment: { id: 0, name: "Commentaire" },
  texte_libre: { id: 1, name: "Reponse libre" },
  note: { id: 2, name: "En note" },
  jalon: { id: 3, name: "En jalon" },
};

const getClassificationColors = (observation) => {
  const normalizedObservation = String(observation || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (normalizedObservation.includes("faible")) {
    return { color: "#b42318", background: "#fff5f4", border: "#fecdca" };
  }
  if (normalizedObservation.includes("non satisfaisant")) {
    return { color: "#b54708", background: "#fffaeb", border: "#fedf89" };
  }
  if (normalizedObservation.includes("satisfaisant")) {
    return { color: "#067647", background: "#f3fcf7", border: "#abefc6" };
  }
  if (normalizedObservation.includes("superieur")) {
    return { color: "#175cd3", background: "#f5f8ff", border: "#b2ccff" };
  }
  if (normalizedObservation.includes("exceptionnel")) {
    return { color: "#6941c6", background: "#faf8ff", border: "#d9d6fe" };
  }
  return { color: "#475467", background: "#f8fafc", border: "#d0d5dd" };
};

const getNoteAppreciation = (note, noteMax) => {
  if (Number(noteMax) <= 0) return "";
  const niveau = Math.ceil((Number(note) / Number(noteMax)) * 5);

  if (niveau <= 0) return "";
  if (niveau === 1) return "Faible";
  if (niveau === 2) return "Non satisfaisant";
  if (niveau === 3) return "Satisfaisant";
  if (niveau === 4) return "Supérieur";
  return "Exceptionnel";
};

const EvaluationExaminationForm = ({ isOpen, candidat, handleClose }) => {
  const { Toast, showToast } = useToast();
  const { DialogComponent, openConfirmDialog } = useConfirm();
  const [openLoadingSimpleBackdrop, setopenLoadingSimpleBackdrop] =
    useState(false);
  const [evaluateurs, setEvaluateurs] = useState([]);
  const [loadingEvaluateurs, setLoadingEvaluateurs] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [evaluateur, setEvaluateur] = useState({});
  const [active_saison, setActiveSaison] = useState({});
  const [selectedCandidat, setSelectedCandidat] = useState(
    candidat || {
      last_name: "",
      first_name: "",
      function_name: "",
      direction_code: "",
      service_code: "",
      sup_f_name: "",
      sup_fonction: "",
      sup_l_name: "",
    },
  );
  const [loadingButton, setLoadingButton] = useState(false);
  const [detailsFicheEvaluation, setDetailsFicheEvaluation] = useState([]);
  const [jalons, setJalons] = useState([]);
  const [isAutoEvalustion, setIsAutoEvalustion] = useState(false);
  const [currentHistory, setCurrentHistory] = useState(null);

  const getInsertableDetailEvaluation = (cloturer = false) => {
    let res = detailsFicheEvaluation.slice();
    return res.map((item, idx) => {
      let reponse = {
        ID_REPONSE: item.ID_REPONSE,
        ID_SAISON: item.ID_SAISON || active_saison.ID_SAISON,
        ID_QUESTIONNAIRE: item.ID_QUESTIONNAIRE,
        ID_TYPE_REPONSE: item.ID_TYPE_REPONSE,
        ID_CANDIDAT: selectedCandidat.user_id,
        TERMINEE: cloturer ? "1" : item.TERMINEE,
        NOTE: item.NOTE || item.NOTE_CA,
        REPONSE:
          Number(item.INTERVIEW_COMMENT_IS_FALLBACK) === 1
            ? item.REPONSE || ""
            : item.REPONSE || item.REP_CA,
        COMMENTAIRE_SUP: item.COMMENTAIRE_SUP ?? item.COMM_CA ?? "",
      };
      if (String(item.TYPE_NAME).includes("note") && item.NOTE === null) {
        return { ...reponse, NOTE: item.NOTE_CA || item.NOTE_MIN };
      } else {
        return reponse;
      }
    });
  };

  const handleFechEvaluateur = () => {
    return UserService.myCompleteUserInfoForEvaluation().then((res) => {
      if (res) {
        setEvaluateur(res.data);
      }
    });
  };

  const handleFechJalonList = (id_fiche) => {
    evaluation_refonte_jalon_service.getListByFiche(id_fiche).then((res) => {
      if (res) {
        setJalons(res.data || []);
      }
    });
  };

  const handleFechFicheEvaluation = (id_evaluateur) => {
    setopenLoadingSimpleBackdrop(true);
    evaluation_refonte_fiche_evaluation_service
      .getAllDetailByUserId(candidat ? candidat.user_id : 0, id_evaluateur)
      .then((res) => {
        if (res) {
          const data = (res.data || []).map((item) =>
            String(item.TYPE_NAME).toLowerCase().includes("note") &&
            (item.NOTE === null || item.NOTE === undefined)
              ? { ...item, NOTE: Number(item.NOTE_CA ?? item.NOTE_MIN ?? 0) }
              : item,
          );
          setDetailsFicheEvaluation(data);
          if (res.data.length > 0) {
            handleFechJalonList(res.data[0].ID_FICHE_EVALUATION);
          } else {
            setJalons([]);
          }
        }
      })
      .catch((error) => {
        showToast(
          TYPE_NOTIFICATION.ERROR,
          error?.message || "Impossible de charger l'évaluation",
        );
      })
      .finally(() => {
        setopenLoadingSimpleBackdrop(false);
      });
  };

  const handleUpdateEvaluationReponse = (obj, new_note_val, type_value) => {
    let idx_pricipal = detailsFicheEvaluation.indexOf(obj);
    let copy_questions = detailsFicheEvaluation.slice();
    if (type_value === TYPE_REPONSE.note) {
      copy_questions[idx_pricipal].NOTE = new_note_val;
    } else if (type_value === TYPE_REPONSE.texte_libre) {
      copy_questions[idx_pricipal].REPONSE = new_note_val;
    } else if (type_value === TYPE_REPONSE.jalon) {
      copy_questions[idx_pricipal].REPONSE = new_note_val;
    } else if (type_value === TYPE_REPONSE.comment) {
      copy_questions[idx_pricipal].COMMENTAIRE_SUP = new_note_val;
    } else {
      copy_questions[idx_pricipal].COMMENTAIRE_SUP = new_note_val;
    }
    setDetailsFicheEvaluation(copy_questions);
  };

  const getCurrentHistoryByCandidatAndEvaluateur = () => {
    return HistoriqueevaluationService.getCurrentHistoryByCandidatAndEvaluateur(
      selectedCandidat?.user_id,
      evaluateur?.user_id,
      active_saison.ID_SAISON,
    ).then((res) => {
      if (res && res.success) {
        setCurrentHistory(res.data);
        return res.data;
      }
      return null;
    });
  };

  const getInsertableHistory = (
    noteGeneral,
    totalMaximum,
    observation,
    terminee = 0,
  ) => {
    return {
      ...currentHistory,
      ID_CANDIDAT: selectedCandidat?.user_id,
      ID_EVALUATEUR: evaluateur?.user_id,
      ID_SESSION: active_saison?.ID_SAISON,
      ID_FICHE:
        detailsFicheEvaluation.length > 0
          ? detailsFicheEvaluation[0].ID_FICHE_EVALUATION
          : null,
      NOTE_GENERAL: noteGeneral,
      OBSERVATION: observation,
      NOTE_GENERAL_MAX: totalMaximum,
      TERMINEE: terminee || 0,
    };
  };

  const handleInsertHistory = (
    noteGeneral,
    totalMaximum,
    observation,
    terminee,
  ) => {
    HistoriqueevaluationService.insertOrUpdate(
      getInsertableHistory(noteGeneral, totalMaximum, observation, terminee),
    ).then((res) => {
      if (res && !res.success) {
        showToast(TYPE_NOTIFICATION.ERROR, res.message);
      }
    });
  };

  const handleSaveAndCloture = (cloturer = false) => {
    setLoadingButton(true);
    evaluation_refonte_reponse_service
      .insert_or_update(getInsertableDetailEvaluation(cloturer))
      .then((res) => {
        if (res && res.success) {
          showToast(TYPE_NOTIFICATION.SUCCESS, res.message);
          handleClose();
        } else {
          showToast(TYPE_NOTIFICATION.ERROR, res.message);
        }
      })
      .finally(() => {
        setLoadingButton(false);
      })
      .catch((e) => {
        showToast(TYPE_NOTIFICATION.ERROR, e.toString());
      });
  };

  const getEvaluationSummaryGroups = () =>
    detailsFicheEvaluation.reduce((groups, item) => {
      const categoryName = item.CATEGORY_NAME || "Sans categorie";
      let categoryGroup = groups.find(
        (group) => group.categoryName === categoryName,
      );

      if (!categoryGroup) {
        categoryGroup = {
          categoryName,
          rows: [],
        };
        groups.push(categoryGroup);
      }

      const hasNote = String(item.TYPE_NAME).toLowerCase().includes("note");
      const note = hasNote
        ? Number(item.NOTE ?? item.NOTE_CA ?? item.NOTE_MIN ?? 0)
        : null;
      const noteMax = hasNote ? Number(item.NOTE_MAX || 0) : null;
      const appreciation = hasNote ? getNoteAppreciation(note, noteMax) : "";
      const appreciationColors = getClassificationColors(appreciation);
      categoryGroup.rows.push({
        id: item.ID_QUESTIONNAIRE,
        question: item.QUESTION,
        hasNote,
        note,
        noteMax,
        appreciation,
        appreciationColors,
        commentaire: item.COMMENTAIRE_SUP ?? item.COMM_CA ?? "",
        reponse: item.REPONSE || item.REP_CA || "",
      });

      return groups;
    }, []);

  const handleSaveAllReponse = (cloturer = false) => {
    const noteDetails = detailsFicheEvaluation.filter((item) =>
      String(item.TYPE_NAME).toLowerCase().includes("note"),
    );
    const totalScore = noteDetails.reduce(
      (sum, item) =>
        sum + Number(item.NOTE ?? item.NOTE_CA ?? item.NOTE_MIN ?? 0),
      0,
    );
    const totalMaximum = noteDetails.reduce(
      (sum, item) => sum + Number(item.NOTE_MAX || 0),
      0,
    );
    const observation =
      totalMaximum > 0
        ? findOutEvaluationGenerale(
            totalScore,
            totalMaximum,
            selectedCandidat.function_id,
          )
        : "Non evaluée";
    const classificationColors = getClassificationColors(observation);
    const evaluationSummaryGroups = getEvaluationSummaryGroups();
    if (cloturer) {
      openConfirmDialog(
        "Confirmation de l'envoi",
        <Grid
          display="flex"
          flexDirection="column"
          gap={1.5}
          sx={{
            flex: { xs: "1 1 auto", sm: "initial" },
            height: { xs: "100%", sm: "auto" },
            minHeight: 0,
            minWidth: { sm: 340 },
            width: "100%",
          }}
        >
          <Typography variant="body2">
            Êtes-vous sûr de vouloir enregistrer et envoyer cette évaluation ?
            Après son envoi, aucune modification ne sera plus possible.
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderColor: "#abefc6",
              borderLeft: "3px solid #00a452",
              bgcolor: "#fbfdfb",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Classification finale
            </Typography>
            <Grid
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              mt={0.5}
            >
              <Typography variant="body2" fontWeight={600}>
                Note globale: {totalScore} / {totalMaximum}
              </Typography>
              <Chip
                size="small"
                label={observation || "Non evaluée"}
                sx={{
                  color: classificationColors.color,
                  bgcolor: classificationColors.background,
                  border: `1px solid ${classificationColors.border}`,
                  fontWeight: 600,
                }}
              />
            </Grid>
          </Paper>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderColor: "#d7dee8",
              bgcolor: "#fff",
              flex: { xs: "1 1 auto", sm: "initial" },
              maxHeight: { xs: "none", sm: 280 },
              minHeight: 0,
              overflowY: "auto",
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Resume des notes et commentaires
            </Typography>
            {evaluationSummaryGroups.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Aucune note renseignee.
              </Typography>
            ) : (
              <Grid display="flex" flexDirection="column" gap={1.25}>
                {evaluationSummaryGroups.map((group, groupIndex) => (
                  <Grid
                    key={`${group.categoryName}-${groupIndex}`}
                    sx={{
                      border: "1px solid #dfe6ef",
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                  >
                    <Grid
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={1}
                      sx={{
                        px: 1,
                        py: 0.75,
                        bgcolor: "#f3f7fb",
                        borderBottom: "1px solid #dfe6ef",
                      }}
                    >
                      <Typography variant="body2" fontWeight={700}>
                        {group.categoryName}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${group.rows.length} element(s)`}
                        sx={{ flexShrink: 0, fontWeight: 600 }}
                      />
                    </Grid>
                    {group.rows.length === 0 ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ p: 1 }}
                      >
                        Aucune note renseignee pour cette categorie.
                      </Typography>
                    ) : (
                      <Grid display="flex" flexDirection="column" gap={1} p={1}>
                        {group.rows.map((row, index) => (
                          <Grid
                            key={row.id || index}
                            sx={{
                              p: 1,
                              border: "1px solid #edf0f4",
                              borderRadius: 1,
                              bgcolor: "#fafbfc",
                            }}
                          >
                            <Grid
                              display="flex"
                              justifyContent="space-between"
                              alignItems="flex-start"
                              gap={1}
                            >
                              <Typography variant="body2" fontWeight={600}>
                                {index + 1}. {row.question}
                              </Typography>
                              <Grid
                                display="flex"
                                flexWrap="wrap"
                                justifyContent="flex-end"
                                gap={0.5}
                                sx={{ flexShrink: 0 }}
                              >
                                <Chip
                                  size="small"
                                  label={
                                    row.hasNote
                                      ? `${row.note} / ${row.noteMax}`
                                      : "Reponse"
                                  }
                                  sx={{ fontWeight: 600 }}
                                />
                                {row.hasNote && (
                                  <Chip
                                    size="small"
                                    label={row.appreciation || "Non evalue"}
                                    sx={{
                                      color: row.appreciationColors.color,
                                      bgcolor:
                                        row.appreciationColors.background,
                                      border: `1px solid ${row.appreciationColors.border}`,
                                      fontWeight: 600,
                                    }}
                                  />
                                )}
                              </Grid>
                            </Grid>
                            <Typography
                              variant="body2"
                              color={
                                row.hasNote
                                  ? row.commentaire
                                    ? "text.primary"
                                    : "text.secondary"
                                  : row.reponse
                                    ? "text.primary"
                                    : "text.secondary"
                              }
                              sx={{ mt: 0.75, whiteSpace: "pre-wrap" }}
                            >
                              {row.hasNote
                                ? row.commentaire || "Aucun commentaire."
                                : row.reponse || "Aucune reponse."}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </Grid>,
        () => {
          handleSaveAndCloture(cloturer);
          handleInsertHistory(totalScore, totalMaximum, observation, 1);
        },
      );
    } else {
      handleSaveAndCloture(cloturer);
      handleInsertHistory(totalScore, totalMaximum, observation, 0);
    }
  };

  const handleCloseDialog = () => {
    handleClose();
    setDetailsFicheEvaluation([]);
  };

  useEffect(() => {
    if (
      !isOpen ||
      !selectedCandidat?.user_id ||
      !evaluateur?.user_id ||
      !active_saison?.ID_SAISON
    )
      return;
    getCurrentHistoryByCandidatAndEvaluateur();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    selectedCandidat?.user_id,
    evaluateur?.user_id,
    active_saison?.ID_SAISON,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setInitializing(true);

    setSelectedCandidat(
      candidat || {
        last_name: "",
        first_name: "",
        function_name: "",
        direction_code: "",
        service_code: "",
        sup_f_name: "",
        sup_fonction: "",
        sup_l_name: "",
      },
    );
    setDetailsFicheEvaluation([]);
    setJalons([]);

    const initEvaluation = async () => {
      const saisonRes = await evaluation_refonte_saison_service.getActiveOne();
      if (cancelled) return;
      const saison = saisonRes?.data || {};
      setActiveSaison(saison);

      try {
        setLoadingEvaluateurs(true);
        const evaluateursRes =
          await evaluation_refonte_reponse_service.getEvaluateurByIdCandidatAndIdSession(
            candidat ? candidat.user_id : 0,
            saison?.ID_SAISON || 0,
          );
        if (cancelled) return;
        const list = evaluateursRes?.data || [];
        const liste_evaluateurs = list.filter(
          (item, index, array) =>
            index === array.findIndex((e) => e.user_id === item.user_id),
        );
        setEvaluateurs(liste_evaluateurs);

        if (list.length > 0) {
          setEvaluateur(Array.from(list).pop());
        } else {
          await handleFechEvaluateur();
        }
      } catch (e) {
        showToast(TYPE_NOTIFICATION.ERROR, e.toString());
      } finally {
        setLoadingEvaluateurs(false);
      }
    };

    initEvaluation().finally(() => {
      if (!cancelled) setInitializing(false);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, candidat]);

  useEffect(() => {
    if (!isOpen || !evaluateur?.user_id) return;
    handleFechFicheEvaluation(evaluateur.user_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, evaluateur?.user_id]);

  useEffect(() => {
    if (!isOpen || !evaluateur?.user_id) return;
    setIsAutoEvalustion(evaluateur?.user_id === candidat?.user_id);
  }, [isOpen, evaluateur?.user_id, candidat?.user_id]);

  const categoryGroups = useMemo(() => {
    const uniqueCategories = Array.from(
      new Set(detailsFicheEvaluation.map((detail) => detail.CATEGORY_NAME)),
    );
    let old_cat = "";
    let categories = uniqueCategories;
    if (isAutoEvalustion) {
      categories = uniqueCategories.filter((category, index) => index !== 3);
    }

    return categories.map((category) => {
      const showRecap =
        String(category).toLowerCase().includes("commentaire") &&
        !String(old_cat).toLowerCase().includes("commentaire");
      old_cat = category;
      return { category, showRecap };
    });
  }, [detailsFicheEvaluation]);

  const isTerminee = useMemo(
    () => detailsFicheEvaluation.some((q) => q.TERMINEE === "1"),
    [detailsFicheEvaluation],
  );

  const isLoadingContent =
    initializing || openLoadingSimpleBackdrop || loadingEvaluateurs;

  return (
    <>
      <Toast />
      <Dialog
        open={isOpen}
        aria-labelledby="dialog-title"
        aria-describedby="dialog-description"
        fullScreen
        // maxWidth="xl"
        slotProps={{
          root: {
            className: "portrait-dialog",
          },
        }}
      >
        <LinearProgress
          color="primary"
          sx={{ display: openLoadingSimpleBackdrop ? "block" : "none" }}
        />
        <DialogComponent />
        <Grid
          height="100dvh"
          minHeight={0}
          display="flex"
          flexDirection="column"
          bgcolor={"#F7F7F8"}
        >
          <DialogTitle
            sx={{
              px: { xs: 2, md: 3 },
              py: 1,
              borderBottom: "1px solid #e1e7ef",
              bgcolor: "#fff",
              flexShrink: 0,
            }}
          >
            <Grid
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
              gap={1.25}
            >
              <Grid minWidth={0}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "#172033", lineHeight: 1.25 }}
                >
                  Formulaire d'evaluation
                </Typography>
              </Grid>
              <Autocomplete
                size="small"
                disablePortal
                options={evaluateurs}
                value={evaluateur || null}
                loading={loadingEvaluateurs}
                getOptionLabel={(option) =>
                  `${option?.first_name || ""} ${option?.last_name || ""}`
                }
                sx={{ width: { xs: "100%", sm: 240, md: 280 }, flexShrink: 0 }}
                renderInput={(params) => (
                  <TextField {...params} label="Evaluateur" size="small" />
                )}
                onChange={(e, value) => {
                  if (value) {
                    setEvaluateur(value);
                  } else {
                    handleFechEvaluateur();
                  }
                }}
              />
            </Grid>
          </DialogTitle>
          <StandardSkeletoon
            col={1}
            row={3}
            m={5}
            boxHeight={"75%"}
            height={35}
            escalier
            show={isLoadingContent}
          />
          <Grid
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: "1 1 0%",
              minHeight: 0,
              height: 0,
            }}
          >
            {isLoadingContent ? null : !(
                !active_saison || detailsFicheEvaluation.length === 0
              ) ? (
              <DialogContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: "1 1 0%",
                  minHeight: 0,
                  height: 0,
                  overflow: "hidden",
                  p: { xs: 1, sm: 2 },
                }}
              >
                <Grid
                  sx={{
                    display: "block",
                    flex: "1 1 0%",
                    minHeight: 0,
                    height: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    overscrollBehaviorY: "contain",
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      mb: 1,
                      overflow: "hidden",
                      border: "1px solid #e1e7ef",
                      borderTop: "2px solid #00a452",
                      borderRadius: 2,
                      boxShadow: "0 1px 3px rgba(24, 39, 75, 0.06)",
                    }}
                  >
                    <Grid className="info-candidat">
                      <Grid
                        display="flex"
                        alignItems="center"
                        gap={1}
                        px={2}
                        py={1.5}
                        sx={{
                          borderBottom: "1px solid #e8edf3",
                          bgcolor: "#fbfdfb",
                        }}
                      >
                        <PersonOutlineIcon
                          fontSize="small"
                          sx={{
                            color: "#00a452",
                            p: 0.4,
                            boxSizing: "content-box",
                            bgcolor: "#edf7f0",
                            borderRadius: 1,
                          }}
                        />
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            color: "text.primary",
                            flex: 1,
                          }}
                        >
                          Identification du poste
                        </Typography>
                      </Grid>
                      <Grid
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, minmax(0, 1fr))",
                            md: "repeat(3, minmax(0, 1fr))",
                          },
                          columnGap: 2,
                          rowGap: 2.5,
                          p: 2,
                        }}
                      >
                        {[
                          {
                            label: "Nom du titulaire du poste",
                            value:
                              `${selectedCandidat.last_name || ""} ${selectedCandidat.first_name || ""}`.trim(),
                          },
                          {
                            label: "Intitulé du poste",
                            value: selectedCandidat.function_name,
                          },
                          /* {
                            label: "Direction",
                            value: selectedCandidat.direction_code,
                          }, */
                          {
                            label: "Direction",
                            value: selectedCandidat.service_code,
                          },
                          {
                            label: "Supérieur hiérarchique",
                            value:
                              `${selectedCandidat.sup_f_name || ""} ${selectedCandidat.sup_l_name || ""}`.trim(),
                          },
                          {
                            label: "Fonction supérieur hiérarchique",
                            value:
                              selectedCandidat.sup_fonction ||
                              evaluateur.function_name,
                          },
                        ].map(({ label, value }) => (
                          <Grid key={label} sx={{ minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mb: 0.5,
                                color: "#718276",
                                fontSize: "0.65rem",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                lineHeight: 1.4,
                              }}
                            >
                              {label}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                minHeight: 26,
                                display: "flex",
                                alignItems: "center",
                                px: 1.25,
                                py: 0.5,
                                border: "1px solid #dfe9e2",
                                borderLeft: "2px solid #a8d5b8",
                                borderRadius: "0 4px 4px 0",
                                bgcolor: "#f8fbf9",
                                color: "text.primary",
                                fontWeight: 500,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {value || "-"}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  </Paper>

                  <Grid className="questionnaire">
                    {categoryGroups.map(({ category, showRecap }, cat_idx) => {
                      return (
                        <>
                          {showRecap && (
                            <RecapEvaluationForm
                              detailsFicheEvaluation={detailsFicheEvaluation}
                              functionId={selectedCandidat.function_id}
                            />
                          )}
                          <Paper
                            key={cat_idx}
                            elevation={0}
                            sx={{
                              display:
                                selectedCandidat.user_id ===
                                  evaluateur.user_id &&
                                detailsFicheEvaluation.find(
                                  (item) => item.CATEGORY_NAME === category,
                                ).VISIBLE_SUP_ONLY === "1"
                                  ? "none"
                                  : "block",
                              mb: 1,
                              mt: 2,
                              border: "1px solid #e1e7ef",
                              borderLeft: "2px solid #00a452",
                              borderRadius: 1.5,
                              bgcolor: "#fff",
                            }}
                          >
                            <Grid m={{ xs: 0.5, sm: 1 }}>
                              <CardHeader
                                title={`${category}`}
                                titleTypographyProps={{
                                  variant: "subtitle1",
                                  sx: { color: "#176b48", fontWeight: 700 },
                                  gutterBottom: true,
                                }}
                                sx={{
                                  py: 1,
                                  borderBottom: "1px solid #edf0f4",
                                }}
                              />
                              {detailsFicheEvaluation
                                .filter(
                                  (item) => item.CATEGORY_NAME === category,
                                )
                                .map((detail, det_idx) => (
                                  <Grid
                                    key={det_idx}
                                    display={"flex"}
                                    flexDirection={"column"}
                                    justifyContent={"center"}
                                  >
                                    <Grid
                                      display={"flex"}
                                      flexDirection={"column"}
                                    >
                                      <Grid>
                                        <CardHeader
                                          titleTypographyProps={{
                                            fontSize: "0.95rem",
                                            fontWeight: 500,
                                          }}
                                          subheaderTypographyProps={{
                                            fontSize: "0.75rem",
                                            fontStyle: "italic",
                                            color: "#8190a5",
                                          }}
                                          title={`${det_idx + 1}. ${
                                            detail.QUESTION
                                          }`}
                                          subheader={`(Type reponse: ${String(
                                            detail.TYPE_NAME,
                                          ).toLowerCase()})`}
                                          sx={{ px: 2, py: 1.25 }}
                                        />
                                      </Grid>
                                    </Grid>
                                    <Grid
                                      display={"flex"}
                                      justifyContent={"center"}
                                      textAlign={"center"}
                                      pb={4}
                                    >
                                      <Grid
                                        display={
                                          detail.TYPE_NAME ===
                                          TYPE_REPONSE.note.name
                                            ? "flex"
                                            : "none"
                                        }
                                        flexDirection={"column"}
                                        width={"100%"}
                                      >
                                        <SliderNotation
                                          disabled={isTerminee}
                                          max_val={Number(detail.NOTE_MAX)}
                                          min_val={Number(detail.NOTE_MIN)}
                                          val={
                                            detail.NOTE === null
                                              ? Number(
                                                  detail.NOTE_CA ||
                                                    detail.NOTE_MIN,
                                                )
                                              : Number(detail.NOTE)
                                          }
                                          on_leave={(n) => {
                                            handleUpdateEvaluationReponse(
                                              detail,
                                              n,
                                              TYPE_REPONSE.note,
                                            );
                                          }}
                                        />
                                        <Grid
                                          container
                                          justifyContent={"center"}
                                        >
                                          <Grid item xs={12} md={6}>
                                            <TextField
                                              disabled={isTerminee}
                                              fullWidth
                                              multiline
                                              sx={{
                                                mr: 3,
                                                mt: 1,
                                                display:
                                                  selectedCandidat.user_id ===
                                                  evaluateur.user_id
                                                    ? "none"
                                                    : "",
                                              }}
                                              minRows={2}
                                              maxRows={10}
                                              label="Votre commentaire"
                                              onChange={(e) => {
                                                handleUpdateEvaluationReponse(
                                                  detail,
                                                  e.target.value,
                                                  TYPE_REPONSE.comment,
                                                );
                                              }}
                                              value={
                                                detail.COMMENTAIRE_SUP ??
                                                detail.COMM_CA ??
                                                ""
                                              }
                                            />
                                          </Grid>
                                        </Grid>
                                      </Grid>
                                      <Grid
                                        className="jalon liste"
                                        display={
                                          detail.TYPE_NAME ===
                                          TYPE_REPONSE.jalon.name
                                            ? "flex"
                                            : "none"
                                        }
                                        flexDirection={"column"}
                                        width={"100%"}
                                      >
                                        {jalons
                                          .filter(
                                            (item) =>
                                              item.ID_QUESTIONNAIRE ===
                                              detail.ID_QUESTIONNAIRE,
                                          )
                                          .map((jalon, j_idx) => (
                                            <Grid
                                              key={j_idx}
                                              ml={4}
                                              display={"flex"}
                                              flexDirection={"row"}
                                              alignItems={"center"}
                                            >
                                              <Grid
                                                display={"flex"}
                                                alignContent={"center"}
                                              >
                                                <Checkbox
                                                  disabled={isTerminee}
                                                  checked={
                                                    detail.REPONSE ===
                                                      jalon.VALEUR ||
                                                    (!detail.REPONSE &&
                                                      detail.REP_CA ===
                                                        jalon.VALEUR)
                                                  }
                                                  onChange={(e) =>
                                                    handleUpdateEvaluationReponse(
                                                      detail,
                                                      e.target.checked
                                                        ? jalon.VALEUR
                                                        : null,
                                                      TYPE_REPONSE.jalon,
                                                    )
                                                  }
                                                />
                                              </Grid>
                                              <Grid
                                                display={"flex"}
                                                flexGrow={1}
                                              >
                                                <Typography>
                                                  {jalon.VALEUR}
                                                </Typography>
                                              </Grid>
                                            </Grid>
                                          ))}
                                      </Grid>
                                      <Grid
                                        width={"100%"}
                                        display={
                                          detail.TYPE_NAME ===
                                          TYPE_REPONSE.texte_libre.name
                                            ? "flex"
                                            : "none"
                                        }
                                        justifyContent={"center"}
                                        flexDirection="column"
                                      >
                                        <TextField
                                          InputProps={{
                                            readOnly:
                                              isTerminee ||
                                              (detail.ORDRE === "3" &&
                                                !isAutoEvalustion),
                                            disabled:
                                              isTerminee ||
                                              (detail.ORDRE === "3" &&
                                                !isAutoEvalustion),
                                          }}
                                          fullWidth
                                          multiline
                                          minRows={2}
                                          maxRows={10}
                                          sx={{
                                            m: 1,
                                            "& textarea": {
                                              resize: "vertical",
                                            },
                                          }}
                                          label={
                                            detail.ORDRE === "3"
                                              ? isAutoEvalustion
                                                ? "Votre reponse"
                                                : "La réponse du consultant"
                                              : "Votre reponse"
                                          }
                                          onChange={(e) =>
                                            handleUpdateEvaluationReponse(
                                              detail,
                                              e.target.value,
                                              TYPE_REPONSE.texte_libre,
                                            )
                                          }
                                          value={
                                            Number(
                                              detail.INTERVIEW_COMMENT_IS_FALLBACK,
                                            ) === 1
                                              ? detail.REPONSE || ""
                                              : detail.REPONSE ||
                                                detail.REP_CA ||
                                                ""
                                          }
                                        />
                                        {Number(
                                          detail.INTERVIEW_COMMENT_IS_FALLBACK,
                                        ) === 1 && (
                                          <Alert severity="info" sx={{ mx: 1 }}>
                                            <AlertTitle>
                                              Dernier commentaire disponible
                                            </AlertTitle>
                                            <Typography
                                              sx={{
                                                whiteSpace: "pre-wrap",
                                                mb: 0.5,
                                              }}
                                            >
                                              {detail.REP_CA}
                                            </Typography>
                                            Entretien avec{" "}
                                            {detail.REP_CA_EVALUATEUR_NAME ||
                                              "évaluateur"}
                                          </Alert>
                                        )}
                                      </Grid>
                                    </Grid>
                                    <Divider
                                      sx={{
                                        display:
                                          detailsFicheEvaluation.filter(
                                            (item) =>
                                              item.CATEGORY_NAME === category,
                                          ).length ===
                                          det_idx + 1
                                            ? "none"
                                            : "flex",
                                        mx: 2,
                                        borderColor: "#e8edf3",
                                      }}
                                      variant="middle"
                                    />
                                  </Grid>
                                ))}
                            </Grid>
                          </Paper>
                        </>
                      );
                    })}
                  </Grid>
                </Grid>
              </DialogContent>
            ) : (
              <DialogContent
                sx={{
                  flex: "1 1 0%",
                  minHeight: 0,
                  height: 0,
                  overflowY: "auto",
                  overscrollBehaviorY: "contain",
                  WebkitOverflowScrolling: "touch",
                  p: { xs: 1, sm: 2 },
                }}
              >
                <Grid width="100%">
                  <Grid display={"block"}>
                    <Alert severity="error">
                      <AlertTitle>Questionnaire non trouvé</AlertTitle>
                      Aucune question disponible pour evaluer cette personne
                      (raison : aucune saison ou fiche d'evaluation active ).
                      Veuillez contacter le responsable de l'evaluation du
                      personnel
                    </Alert>
                  </Grid>
                </Grid>
              </DialogContent>
            )}
          </Grid>
          <DialogActions
            sx={{
              flexShrink: 0,
              flexDirection: { xs: "column-reverse", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              gap: 1,
              px: { xs: 2, sm: 3 },
              py: 1.5,
              borderTop: "1px solid #e1e7ef",
              bgcolor: "#fff",
              "& > *": {
                width: { xs: "100%", sm: "auto" },
                m: "0 !important",
              },
            }}
          >
            <LoadingButton
              loading={loadingButton}
              disabled={
                isTerminee ||
                detailsFicheEvaluation.length === 0 ||
                isLoadingContent
              }
              onClick={(e) => handleSaveAllReponse(false)}
              color="primary"
              variant="contained"
            >
              Enregistrer
            </LoadingButton>
            <LoadingButton
              loading={loadingButton}
              disabled={
                isTerminee ||
                detailsFicheEvaluation.length === 0 ||
                isLoadingContent
              }
              onClick={(e) => handleSaveAllReponse(true)}
              color="secondary"
              variant="contained"
            >
              Enregistrer & envoyer
            </LoadingButton>
            <Button
              onClick={(e) => handleCloseDialog()}
              color="primary"
              variant="outlined"
            >
              Fermer
            </Button>
          </DialogActions>
        </Grid>
      </Dialog>
    </>
  );
};

export default EvaluationExaminationForm;
